import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import VLFCard from "@/components/Card";
import DataTable from "@/components/DataTable";
import Kpi, {KpiRow} from "@/components/Kpi";
import {DsBar} from "@/components/DsKit";

import {
	injuryDaysOut,
	isInjuryActiveAt,
	loadMedicalRosterRecords,
} from "@/api/medical";
import {ERROR_OPERATION} from "@/utils/constants";

import "./index.scss";

const isActive = (inj) => isInjuryActiveAt(inj, moment());

const frequency = (items, key) => {
	const counts = {};
	for (const item of items) {
		const val = item[key];
		if (val) counts[val] = (counts[val] || 0) + 1;
	}
	return Object.entries(counts)
		.sort((a, b) => b[1] - a[1])
		.slice(0, 6);
};

function MedicalOverviewPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(true);
	const [records, setRecords] = useState([]);

	useEffect(() => {
		let active = true;
		setLoading(true);
		setRecords([]);
		loadMedicalRosterRecords(seasonId)
			.then((data) => {
				if (active) setRecords(data);
			})
			.catch(() => {
				if (active) handleNotification(ERROR_OPERATION, "error");
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [seasonId]);

	const allInjuries = records.flatMap((r) =>
		(r.injuriesIllness ?? [])
			.filter((i) => !i.recordType)
			.map((i) => ({
				...i,
				playerData: r.playerData,
				recordId: r.id,
				currentRosters: r.currentRosters,
			})),
	);

	const currentlyInjured = allInjuries.filter(isActive);

	// Group currently injured by player (a player can have multiple active injuries)
	const injuredPlayers = Object.values(
		currentlyInjured.reduce((acc, inj) => {
			const pid = inj.playerData?.id ?? inj.playerId;
			if (!acc[pid]) {
				acc[pid] = {
					playerData: inj.playerData,
					recordId: inj.recordId,
					currentRosters: inj.currentRosters,
					injuries: [],
				};
			}
			acc[pid].injuries.push(inj);
			return acc;
		}, {}),
	);

	const totalDaysOut = allInjuries.reduce(
		(sum, inj) => sum + (injuryDaysOut(inj) || 0),
		0,
	);
	const avgDays =
		allInjuries.length > 0 ? Math.round(totalDaysOut / allInjuries.length) : 0;

	const bodyPartFreq = frequency(allInjuries, "bodyPart");
	const typeFreq = frequency(allInjuries, "injuryType");
	const causeFreq = frequency(allInjuries, "cause");

	const rosterMap = new Map();
	for (const inj of currentlyInjured) {
		for (const roster of inj.currentRosters || []) {
			const entry = rosterMap.get(roster.id) || {...roster, count: 0};
			entry.count += 1;
			rosterMap.set(roster.id, entry);
		}
	}
	const rosterEntries = [...rosterMap.values()].sort(
		(a, b) => b.count - a.count,
	);

	const freqSections = [
		{title: "Parti del corpo più colpite", rows: bodyPartFreq},
		{title: "Tipi di infortunio", rows: typeFreq},
		{title: "Cause principali", rows: causeFreq},
		{
			title: "Infortunati per rosa",
			rows: rosterEntries.map((r) => [r.name, r.count]),
		},
	].filter((f) => f.rows.length);

	const injuredColumns = [
		{
			key: "name",
			label: "Giocatore",
			render: (row) =>
				[row.playerData?.firstName, row.playerData?.lastName]
					.filter(Boolean)
					.join(" "),
			sortValue: (row) => row.playerData?.lastName,
		},
		{
			key: "rosters",
			label: "Rosa",
			render: (row) => row.currentRosters?.map((r) => r.name).join(", "),
		},
		{
			key: "diagnosis",
			label: "Infortunio",
			render: (row) =>
				row.injuries
					.map((inj) => inj.diagnosis || inj.injuryType || "Infortunio")
					.join(", "),
		},
		{
			key: "days",
			label: "Giorni fuori",
			align: "end",
			render: (row) => row.days,
			sortValue: (row) => row.days,
		},
	];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs current="Panoramica medica" />
			<div className="medical-overview">
				<KpiRow>
					<Kpi
						label="Infortunati oggi"
						value={injuredPlayers.length}
						tone={injuredPlayers.length ? "critical" : "good"}
					/>
					<Kpi label="Infortuni in stagione" value={allInjuries.length} />
					<Kpi label="Giorni fuori" value={totalDaysOut} />
					<Kpi label="Media giorni per infortunio" value={avgDays} />
				</KpiRow>

				<VLFCard
					title="Infortunati oggi"
					content={
						<DataTable
							columns={injuredColumns}
							rows={injuredPlayers.map((p) => ({
								...p,
								id: p.recordId,
								days: Math.max(...p.injuries.map((inj) => injuryDaysOut(inj))),
							}))}
							onRowClick={(row) => navigate("/medical/records/" + row.recordId)}
							empty="Nessun giocatore infortunato."
						/>
					}
				/>

				{freqSections.length > 0 && (
					<div className="medical-overview__grid">
						{freqSections.map((f) => {
							const max = f.rows[0][1];
							return (
								<div key={f.title}>
									<VLFCard
										title={f.title}
										content={
											<div className="medical-overview__freq">
												{f.rows.map(([label, count]) => (
													<div
														key={label}
														className="medical-overview__freq-row"
													>
														<span className="medical-overview__freq-label">
															{label}
														</span>
														<DsBar value={count} max={max} />
														<span className="medical-overview__freq-count">
															{count}
														</span>
													</div>
												))}
											</div>
										}
									/>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</Page>
	);
}

export default MedicalOverviewPage;
